import Link from 'next/link';
import { useCallback, useMemo, useState } from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import { modalClasses } from '@mui/material/Modal';
import { useTranslations } from 'next-intl';
import { useBoolean, useCopyToClipboard } from 'usehooks-ts';

import { Avatar, Button, IconButton, showSnackbar } from '@web/ui/components';
import {
  ArrowLinkIcon,
  CheckIcon,
  CloseIcon,
  LanguageIcon,
} from '@web/ui/icons';
import { ThemeModeSwitcher } from '@web/ui/theme/ThemeModeSwitcher';
import { KThemeMode, useDesignsTokenTheme } from '@web/ui/theme/ThemeProvider';
import { transientOptions } from '@web/ui/theme/transientOptions';
import { signOut } from '@web/utils/auth/service';
import { sliceWalletAddress } from '@web/utils/wallet';

import { OPENSEA_PERSONA_BASE, OPENSEA_PERSONA_ETH } from '../const';
import { useLocale } from '../i18n/NextIntlProvider';
import { ICompletedUser } from '../user/service';

import { ConnectArea } from './ConnectArea';
import { SocialLinks } from './SocialLinks';
import { env } from '@web/utils/env';

/* Styled Components */

const Container = styled(Drawer, transientOptions)<{ $connected: boolean }>`
  .${modalClasses.backdrop} {
    background-color: ${({ theme }) => theme.color.backdrop};
  }

  .${drawerClasses.paper} {
    height: auto;
    inset: 0;
    left: unset;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing('3000')};
    padding-top: ${({ theme }) => theme.spacing('1500')};
    padding-bottom: ${({ theme, $connected }) =>
      $connected ? theme.spacing('1500') : theme.spacing('0500')};
    border-top-left-radius: ${({ theme }) => theme.spacing('1250')};
    border-bottom-left-radius: ${({ theme }) => theme.spacing('1250')};
    background-color: ${({ theme }) => theme.color.background};
    box-shadow: ${({ theme }) => theme.elevation.level5};
    box-sizing: border-box;
    overflow-x: hidden;
    width: 100%;
    max-width: 280px;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      max-width: 340px;
      inset: ${({ theme }) => theme.spacing('0500')};
      left: unset;
      border-top-right-radius: ${({ theme }) => theme.spacing('1250')};
      border-bottom-right-radius: ${({ theme }) => theme.spacing('1250')};
    }
  }
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  z-index: 5;
  top: ${({ theme }) => theme.spacing('0500')};
  right: ${({ theme }) => theme.spacing('0500')};
`;

const SlideContainer = styled.div<{ pushed: boolean }>`
  width: 200%;
  flex-grow: 2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  transition: transform 300ms ease-in-out;
  transform: translateX(${({ pushed }) => (pushed ? '-50%' : '0')});
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('3000')};
  padding-inline: ${({ theme }) => theme.spacing('1500')};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: ${({ theme }) => theme.spacing('1000')};
`;

const AvatarLink = styled(Link)`
  position: relative;
  overflow: hidden;
  outline: ${({ theme }) =>
    `${theme.color.onBackground} solid ${theme.outline['1']}`};
  transition: all 200ms ease;
  border-radius: ${({ theme }) => theme.shape.corner['0750']};

  :hover {
    border-radius: 50%;
  }

  :active {
    :before {
      content: '';
      position: absolute;
      inset: 0;
      background: ${({ theme }) => theme.color.onBackground};
      opacity: ${({ theme }) => theme.opacity.semiTransparent};
    }
  }
`;

const User = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('0625')};
`;

const UserName = styled.span`
  color: ${({ theme }) => theme.color.onBackground};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['4']};
  text-transform: ${({ theme }) => theme.textTransform.capitalize};

  :hover {
    text-decoration: ${({ theme }) => theme.textDecoration.underline};
  }
`;

const WalletAddress = styled.span`
  color: ${({ theme }) => theme.color.onBackground};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  opacity: ${({ theme }) => theme.opacity.dense};
  cursor: pointer;

  :hover {
    text-decoration: ${({ theme }) => theme.textDecoration.underline};
  }
`;

const ItemList = styled.div<{ connected: boolean }>`
  padding-top: ${({ theme, connected }) =>
    connected ? 0 : theme.spacing('1000')};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('3000')};
`;

const ItemGroup = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('1500')};
`;

const Item = styled.li<{ disabled?: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing('1500')};
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.color.onBackground};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['2']};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.default};

  opacity: ${({ disabled, theme }) => disabled && theme.opacity.solid};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
    `}
`;

const BackButton = styled(Button)`
  align-self: start;
`;

const SubMenuTitle = styled.span`
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0750']};

  &:after {
    content: ':';
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

const ButtonLabel = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

const Footer = styled.div<{ connected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('1750')};
  justify-content: stretch;
  padding-inline: ${({ theme, connected }) =>
    connected ? theme.spacing('1500') : theme.spacing('0500')};
  position: ${({ connected }) => (connected ? 'relative' : 'sticky')};
  bottom: ${({ connected }) => (connected ? 'auto' : 0)};
`;

const DisclaimerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing('1000')};
  padding-inline: ${({ theme }) => theme.spacing('1500')};
`;

const DisclaimerLink = styled(Link)`
  color: ${({ theme }) => theme.color.onBackground};
  opacity: ${({ theme }) => theme.opacity.dense};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.default};

  :hover {
    text-decoration: ${({ theme }) => theme.textDecoration.underline};
  }
`;

/* Main Components */

type KSubMenu = 'LANG' | 'THEME';

export interface ISideSheetProps {
  user: ICompletedUser | undefined;
  open: boolean;
  onClose(): void;
}

export function SideSheet({ user, open, onClose }: ISideSheetProps) {
  const t = useTranslations();
  const [, copy] = useCopyToClipboard();

  const handleCopyWalletAddress = useCallback(() => {
    if (!user) {
      return;
    }

    showSnackbar(t('WALLET_COPY_SUCCESS'));
    copy(user.displayWallet);
  }, [user?.displayWallet]);

  const [subMenu, setSubMenu] = useState<KSubMenu>();
  const {
    value: isSubMenuOpen,
    setTrue: openSubMenu,
    setFalse: closeSubMenu,
  } = useBoolean(false);
  const { locale, availableLocales, setLocale } = useLocale();

  const { theme, switchTheme, availableThemes } = useDesignsTokenTheme();

  const handleClose = () => {
    setSubMenu(undefined);
    closeSubMenu();
    onClose();
  };

  const handleOpenSubMenu = (next: KSubMenu) => {
    setSubMenu(next);
    openSubMenu();
  };
  const handleCloseSubMenu = () => {
    closeSubMenu();
    setTimeout(() => setSubMenu(undefined), 300);
  };

  const handleDisconnect = () => {
    handleClose();
    signOut();
  };

  const formattedWalletAddress = useMemo(
    () => user && sliceWalletAddress(user.displayWallet),
    [user],
  );

  return (
    <Container
      open={open}
      anchor="right"
      onClose={handleClose}
      $connected={!!user}
    >
      <CloseButton onClick={handleClose}>
        <CloseIcon color="onBackground" />
      </CloseButton>
      <SlideContainer pushed={isSubMenuOpen}>
        <Content>
          {user && (
            <Header as="span">
              <AvatarLink
                href={`/profile?u=${user.twitter.handle}`}
                onClick={handleClose}
              >
                <Avatar
                  src={user.twitter.profilePictureURL}
                  size="7000"
                  borderRadius="0750"
                  alt={`${user.twitter.displayName}'s avatar`}
                  tokenId={user.twitter.personaPFPTokenId}
                />
              </AvatarLink>
              <User>
                <UserName>{user.twitter.displayName}</UserName>
                <WalletAddress onClick={handleCopyWalletAddress}>
                  {formattedWalletAddress}
                </WalletAddress>
              </User>
            </Header>
          )}
          <ItemList connected={!!user}>
            <ItemGroup>
              <Link href="/" onClick={handleClose}>
                <Item>{t('SIDE_SHEET-HOME')}</Item>
              </Link>
              {user && (
                <Link href="/settings" onClick={handleClose}>
                  <Item>{t('SIDE_SHEET-SETTINGS')}</Item>
                </Link>
              )}
            </ItemGroup>
            <ItemGroup>
              {/* /quests page removed during migration. Program and Staking
                  are account surfaces and are not offered in the public
                  build; Lore and the manga take their place. */}
              {!env.SHELL_MODE && (
                <>
                  <Link href="/program" onClick={handleClose}>
                    <Item>{t('SIDE_SHEET-PROGRAM')}</Item>
                  </Link>
                  <Link href="/staking" onClick={handleClose}>
                    <Item>{t('SIDE_SHEET-STAKING')}</Item>
                  </Link>
                </>
              )}
              <Link href="/collection" onClick={handleClose}>
                <Item>{t('SIDE_SHEET-COLLECTION')}</Item>
              </Link>
              {env.SHELL_MODE && (
                <>
                  <Link href="/lore" onClick={handleClose}>
                    <Item>{t('HUB_LORE_TAB')}</Item>
                  </Link>
                  <Link href="/manga" onClick={handleClose}>
                    <Item>{t('W_MANGA')}</Item>
                  </Link>
                  <Link href="/council" onClick={handleClose}>
                    <Item>{t('HUB_COUNCIL_TITLE')}</Item>
                  </Link>
                </>
              )}
            </ItemGroup>
            <ItemGroup>
              <Item onClick={() => handleOpenSubMenu('LANG')}>
                {t('SIDE_SHEET-LANGUAGE')}
                <LanguageIcon size="1125" />
              </Item>
              <ThemeModeSwitcher
                mode={theme.mode}
                onChange={(mode: KThemeMode) => switchTheme({ mode })}
              />
            </ItemGroup>
            {user ? (
              <ItemGroup>
                <Item onClick={handleDisconnect}>
                  {t('SIDE_SHEET-DISCONNECT')}
                </Item>
              </ItemGroup>
            ) : (
              <SocialLinks />
            )}
          </ItemList>
        </Content>

        {subMenu === 'LANG' && (
          <Content>
            <BackButton
              label={t('SIDE_SHEET-BACK')}
              variant="secondary"
              background="background"
              color="onBackground"
              onClick={handleCloseSubMenu}
            />
            <SubMenuTitle>{t('SIDE_SHEET-LANG_SWITCH')}</SubMenuTitle>
            <ItemGroup>
              {availableLocales.map(({ label, value }) => (
                <Item
                  key={value}
                  role="button"
                  onClick={() => setLocale(value)}
                >
                  {label}
                  {locale === value && <CheckIcon size="1125" />}
                </Item>
              ))}
            </ItemGroup>
          </Content>
        )}
        {subMenu === 'THEME' && (
          <Content>
            <BackButton
              label={t('SIDE_SHEET-BACK')}
              variant="secondary"
              background="background"
              color="onBackground"
              onClick={handleCloseSubMenu}
            />
            <SubMenuTitle>{t('SIDE_SHEET-THEME_SWITCH')}</SubMenuTitle>

            <ThemeModeSwitcher
              mode={theme.mode}
              onChange={(mode: KThemeMode) => switchTheme({ mode })}
            />

            <ItemGroup>
              {availableThemes.map((variant) => (
                <Item
                  key={variant}
                  role="button"
                  onClick={() => switchTheme({ variant })}
                >
                  {t(`THEME_VARIANT-${variant}`)}
                  {theme.variant === variant && <CheckIcon size="1125" />}
                </Item>
              ))}
            </ItemGroup>
          </Content>
        )}
      </SlideContainer>

      <DisclaimerContainer>
        <DisclaimerLink href="/privacy" onClick={handleClose}>
          {t('SIDE_SHEET_PRIVACY')}
        </DisclaimerLink>
        <DisclaimerLink href="/terms" onClick={handleClose}>
          {t('SIDE_SHEET_TERMS')}
        </DisclaimerLink>
      </DisclaimerContainer>

      <Footer connected={!!user}>
        {user ? (
          <>
            <SocialLinks />
            <Actions>
              <Link href={OPENSEA_PERSONA_ETH} rel="noreferrer" target="_blank">
                <Button
                  label={
                    <ButtonLabel>
                      {t('JOIN_THE_FAMILY_HUB_TILE_BUTTON_ETH')}
                      <ArrowLinkIcon size="1000" />
                    </ButtonLabel>
                  }
                  variant="secondary"
                  color="onPink"
                  background="pink"
                  fullWidth
                />
              </Link>
              <Link
                href={`${OPENSEA_PERSONA_BASE}`}
                rel="noreferrer"
                target="_blank"
              >
                <Button
                  label={
                    <ButtonLabel>
                      {t('JOIN_THE_FAMILY_HUB_TILE_BUTTON_BASE')}
                      <ArrowLinkIcon size="1000" />
                    </ButtonLabel>
                  }
                  variant="secondary"
                  color="onBlue"
                  background="blue"
                  borderColor="onSurface"
                  fullWidth
                />
              </Link>
            </Actions>
          </>
        ) : (
          <ConnectArea />
        )}
      </Footer>
    </Container>
  );
}
